import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Progress } from '../../components/ui/progress';
import { 
  Cloud, Package, FileText, AlertCircle, CheckCircle2, Zap, Shield, 
  Sparkles, MessageSquare, Rocket, Users, Plus, Loader2, ExternalLink,
  HardDrive, Settings
} from 'lucide-react';

// Helper to format bytes to GB
const formatStorageGB = (bytes) => {
  if (!bytes || bytes === 0) return '0';
  const gb = bytes / (1024 * 1024 * 1024);
  // For very small values, show MB instead
  if (gb < 0.01) {
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(1) + ' MB';
  }
  return gb < 1 ? gb.toFixed(2) : gb.toFixed(1);
};

// Helper to format storage display (GB or MB depending on size)
const formatStorageDisplay = (bytes, totalGB) => {
  if (!bytes || bytes === 0) return `0 / ${totalGB} GB`;
  const gb = bytes / (1024 * 1024 * 1024);
  // For very small values, show MB
  if (gb < 0.01) {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB / ${totalGB} GB`;
  }
  const gbStr = gb < 1 ? gb.toFixed(2) : gb.toFixed(1);
  return `${gbStr} / ${totalGB} GB`;
};

// Helper to calculate storage percentage
const getStoragePercent = (usedBytes, totalGB) => {
  if (!usedBytes || !totalGB) return 0;
  const totalBytes = totalGB * 1024 * 1024 * 1024;
  const percent = (usedBytes / totalBytes) * 100;
  // For very small percentages, show at least 0.01%
  if (percent > 0 && percent < 0.1) {
    return Math.round(percent * 100) / 100; // 2 decimals for tiny values
  }
  return Math.min(Math.round(percent * 10) / 10, 100); // 1 decimal normally
};

export const PortalDashboard = () => {
  const { token } = useAuth();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/user/instances`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Instanzen konnten nicht geladen werden');
        }
        
        const data = await response.json();
        setInstances(data.instances || []);
      } catch (err) {
        console.error('Error fetching instances:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInstances();
    }
  }, [token]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-500/10 text-green-600 border-green-500/30';
      case 'failed':
        return 'bg-red-500/10 text-red-600 border-red-500/30';
      default:
        return 'bg-amber-500/10 text-amber-600 border-amber-500/30';
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      {/* Welcome */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-lg shadow-primary/30">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-semibold">
              Willkommen zurück, Held! 🦸
            </h1>
            <p className="mt-1 text-muted-foreground">
              Hier sind deine Wolken-Festungen auf einen Blick.
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Lade deine Helden-Wolken...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="glass-card border-red-500/30 bg-red-500/5">
          <CardContent className="py-6 text-center">
            <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-2" />
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* No Instances State */}
      {!loading && !error && instances.length === 0 && (
        <Card className="glass-card border-primary/20">
          <CardContent className="py-12 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-blue-500/20 mx-auto mb-4">
              <Cloud className="h-10 w-10 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Noch keine Helden-Wolke? 🦸</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Starte jetzt deine erste Managed Nextcloud und werde zum Cloud-Helden!
            </p>
            <Button asChild className="bg-gradient-to-r from-primary to-blue-600">
              <Link to="/checkout" className="flex items-center gap-2">
                <Rocket className="h-4 w-4" />
                Erste Wolke erstellen
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instances Grid */}
      {!loading && instances.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <Cloud className="h-5 w-5 text-primary" />
              Deine Helden-Wolken ({instances.length})
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            {instances.map((instance) => (
              <Card 
                key={instance.id} 
                className="glass-card border-primary/20 overflow-hidden hover:shadow-lg transition-all"
                data-testid={`instance-card-${instance.id}`}
              >
                <div className={`h-1 ${instance.cancellation_pending ? 'bg-gradient-to-r from-red-500 to-red-700' : instance.status === 'ready' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`} />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Cloud className="h-5 w-5 text-primary" />
                      {instance.name || 'Meine Wolke'}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {instance.cancellation_pending && (
                        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
                          Gekündigt
                        </Badge>
                      )}
                      <Badge className={getStatusColor(instance.status)}>
                        {instance.cancellation_pending && instance.status === 'ready' ? 'Bereit' : instance.status_label}
                      </Badge>
                    </div>
                  </div>
                  {instance.domain && (
                    <CardDescription className="truncate">{instance.domain}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Storage Usage */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <HardDrive className="h-4 w-4" />
                        Speicher
                      </span>
                      <span className="text-muted-foreground">
                        {formatStorageDisplay(instance.storage_used_bytes, instance.storage_gb)}
                        {' '}({getStoragePercent(instance.storage_used_bytes, instance.storage_gb)}%)
                      </span>
                    </div>
                    <Progress 
                      value={getStoragePercent(instance.storage_used_bytes, instance.storage_gb)} 
                      className="h-2"
                    />
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {instance.collabora_users} Office-Nutzer
                    </div>
                    {instance.hp_backend && (
                      <div className="flex items-center gap-2 text-amber-500">
                        <Zap className="h-4 w-4" />
                        HPB Talk
                      </div>
                    )}
                    {instance.antivirus && (
                      <div className="flex items-center gap-2 text-green-500">
                        <Shield className="h-4 w-4" />
                        Schutzschild
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm font-medium text-primary">
                      {instance.monthly_price?.toFixed(2)} €/Monat
                    </span>
                    <div className="flex items-center gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/portal/instanz/${instance.id}`} className="flex items-center gap-1">
                          <Settings className="h-3 w-3" />
                          Details
                        </Link>
                      </Button>
                      {instance.status === 'ready' && instance.url && (
                        <Button asChild size="sm" className="bg-gradient-to-r from-primary to-blue-600">
                          <a href={instance.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" />
                            Öffnen
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

          </div>
        </>
      )}

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Invoices Card */}
        <Card className="glass-card" data-testid="dashboard-invoices-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Helden-Rechnungen
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              📄 Alle deine Rechnungen an einem Ort.
            </p>
            <Button asChild variant="outline" size="sm" className="w-full">
              <Link to="/portal/rechnungen">Rechnungen ansehen</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Support Card */}
        <Card className="glass-card bg-gradient-to-r from-primary/5 to-blue-500/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Brauchst du Helden-Support? 🦸‍♂️
            </CardTitle>
            <CardDescription>
              Unser Helden-Team steht dir zur Seite.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="bg-gradient-to-r from-primary to-blue-600">
              <Link to="/portal/support" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Helden-Ticket erstellen
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
